import { PostData } from "./FetchData";

const MS1 = "ms1";
const BASE = "api/v1/website";

function extractMessage(payload, fallback) {
	if (typeof payload === "string" && payload.trim()) return payload;
	if (payload && typeof payload.message === "string" && payload.message.trim()) {
		return payload.message;
	}
	if (payload?.message && typeof payload.message.message === "string") {
		return payload.message.message;
	}
	if (payload && typeof payload.detail === "string" && payload.detail.trim()) {
		return payload.detail;
	}
	return fallback;
}

function parseWpResponse(res, fallbackMessage = "Request failed") {
	if (res === "Invalid" || res === undefined || res === null) {
		return { ok: false, message: fallbackMessage, data: null };
	}
	if (res.status === "auth_error") {
		return { ok: false, message: extractMessage(res, fallbackMessage), data: null };
	}
	if (
		res.status === false ||
		res.status === "failure" ||
		res.status === "api_error" ||
		res.status === "server_error" ||
		res.status === "application_error" ||
		res.status === "not_found_error"
	) {
		return {
			ok: false,
			message: extractMessage(res.message ?? res, fallbackMessage),
			data: res.data ?? null,
		};
	}
	if (res.status !== "success" && res.status !== true && res.status !== "connected") {
		return {
			ok: false,
			message: extractMessage(res.message ?? res, fallbackMessage),
			data: null,
		};
	}
	return {
		ok: true,
		message: extractMessage(res, null),
		data: res.data ?? res,
	};
}

function requireWebsiteId(websiteId) {
	if (websiteId == null || websiteId === "") {
		return { ok: false, message: "websiteId is required", data: null };
	}
	return null;
}

function credentialsBody(websiteId, siteUrl, username, appPassword) {
	return {
		websiteId: Number(websiteId),
		site_url: siteUrl,
		username,
		app_password: appPassword,
	};
}

/**
 * Validate WordPress credentials before saving (sbend proxy).
 */
export async function testWordPressConnection({
	websiteId,
	siteUrl,
	username,
	appPassword,
}) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	const res = await PostData(
		MS1,
		`${BASE}/wordpressTestConnection`,
		credentialsBody(websiteId, siteUrl, username, appPassword)
	);
	return parseWpResponse(res, "WordPress connection test failed");
}

/**
 * Persist WordPress integration for the current website (sbend proxy).
 * App password is not returned by the API.
 */
export async function saveWordPressIntegration({
	websiteId,
	siteUrl,
	username,
	appPassword,
}) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	const res = await PostData(
		MS1,
		`${BASE}/wordpressAddIntegration`,
		credentialsBody(websiteId, siteUrl, username, appPassword)
	);
	const parsed = parseWpResponse(res, "Could not save WordPress integration");
	if (!parsed.ok) return parsed;
	return {
		ok: true,
		message: parsed.message || "WordPress integration saved",
		data: parsed.data,
	};
}

/**
 * Load existing WordPress integration for the current website (sbend proxy).
 * Empty state is a success envelope with connected:false (not a 404 error).
 */
export async function getWordPressIntegration(websiteId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, connected: false, integration: null };
	const res = await PostData(MS1, `${BASE}/wordpressGetIntegration`, {
		websiteId: Number(websiteId),
	});
	const parsed = parseWpResponse(res, "Failed to load WordPress integration");
	if (!parsed.ok) {
		return { ...parsed, connected: false, integration: null };
	}

	const payload = parsed.data;
	const connected = Boolean(payload?.connected);
	const raw = payload?.integration ?? null;

	if (!connected || !raw) {
		return {
			ok: true,
			message: parsed.message,
			connected: false,
			integration: null,
		};
	}

	return {
		ok: true,
		message: parsed.message,
		connected: true,
		integration: {
			id: raw.id ?? null,
			siteUrl: raw.site_url || raw.siteUrl || "",
			username: raw.username || "",
			createdAt: raw.created_at || raw.createdAt || null,
			updatedAt: raw.updated_at || raw.updatedAt || null,
		},
	};
}
